import { useEffect, useState } from 'react';
import { Box, Button, Heading, Text } from '@primer/react';
import { ArrowClockwise, Spinner, Warning, WarningCircle, Info } from 'phosphor-react';
import Accordion from '@/components/Elements/Accordion/Accordion';
import { useGetAiAssistLogs } from '../api/getAiAssistLogs';

interface AiAssistLogsProps {
  clusterId: string;
  isAccordionOpen: boolean;
  projectId: any;
  deploymentStatus: any;
}

export const AiAssistLogs = ({
  clusterId,
  isAccordionOpen,
  projectId,
  deploymentStatus,
}: AiAssistLogsProps) => {
  // Local state for the analysis details.
  const [analysis, setAnalysis] = useState<any>(null);

  // Hook for calling the AI Assist API.
  const { mutate, data, isLoading, isError, error } = useGetAiAssistLogs();

  // When deploymentStatus is "stopped" or not present, remove AI suggestions from localStorage.
  useEffect(() => {
    if (!deploymentStatus || deploymentStatus === 'stopped') {
      const projectStateStr = localStorage.getItem('projectState');
      if (projectStateStr) {
        let projectState = JSON.parse(projectStateStr);
        if (projectState[projectId] && projectState[projectId].aiSuggestions) {
          delete projectState[projectId].aiSuggestions;
          localStorage.setItem('projectState', JSON.stringify(projectState));
        }
      }
      setAnalysis(null);
    }
  }, [deploymentStatus, projectId]);

  // On mount or when the tab is opened, check localStorage.
  useEffect(() => {
    if (
      isAccordionOpen &&
      clusterId &&
      projectId &&
      deploymentStatus &&
      deploymentStatus !== 'stopped'
    ) {
      const projectStateStr = localStorage.getItem('projectState');
      let projectState = projectStateStr ? JSON.parse(projectStateStr) : {};
      if (projectState[projectId] && projectState[projectId].aiSuggestions) {
        setAnalysis(projectState[projectId].aiSuggestions);
      } else {
        // Auto-generate and store in localStorage if no suggestion exists.
        mutate({ cluster_id: clusterId });
      }
    }
  }, [isAccordionOpen, clusterId, projectId, deploymentStatus, mutate]);

  // When the API call returns, update state and localStorage.
  useEffect(() => {
    if (data && data.data && data.data.ai_analysis && data.data.ai_analysis.analysis) {
      const newAnalysis = data.data.ai_analysis.analysis;
      setAnalysis(newAnalysis);
      const projectStateStr = localStorage.getItem('projectState');
      let projectState = projectStateStr ? JSON.parse(projectStateStr) : {};
      if (!projectState[projectId]) {
        projectState[projectId] = {};
      }
      projectState[projectId].aiSuggestions = newAnalysis;
      localStorage.setItem('projectState', JSON.stringify(projectState));
    }
  }, [data, projectId]);

  // Handle the "Regenerate" button click.
  const handleRegenerate = () => {
    mutate({ cluster_id: clusterId });
  };

  if (!isAccordionOpen) return null;

  if (isLoading) {
    return (
      <Box className="flex justify-center my-auto h-full bg-black items-center gap-2 p-4">
        <Spinner size={20} className="animate-spin" />
        <Text>Analyzing logs with AI...</Text>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className="p-4 flex items-center justify-center w-full h-full bg-black text-red-500">
        Failed to get AI analysis. Please try again later.
      </Box>
    );
  }

  if (!analysis) {
    return (
      <Box className="p-4 bg-black flex items-center justify-center w-full h-full text-gray-400">
        No AI analysis available
      </Box>
    );
  }

  return (
    <Box
      className="flex flex-col gap-4 p-4 text-wrap bg-black relative overflow-y-auto h-full overflow-x-hidden"
      style={{ height: 313 }}
    >
      <Button
        leadingVisual={ArrowClockwise}
        variant="primary"
        size="small"
        onClick={handleRegenerate}
        className="absolute top-4 right-4 z-10"
        disabled={isLoading}
      >
        Regenerate
      </Button>
      <Box className="flex flex-col gap-2">
        <Heading as="h6">AI Log Analysis Summary</Heading>
        <Text className="text-gray-300">{analysis.analysis_summary}</Text>
        <Box className="flex items-center gap-2">
          <Text fontWeight="bold" className="text-gray-400">
            Total issues found:
          </Text>
          <Box
            className="px-2 py-1 rounded"
            sx={{
              backgroundColor: analysis.error_count > 0 ? 'attention.emphasis' : 'success.emphasis',
              color: 'fg.onEmphasis',
            }}
          >
            {analysis.error_count}
          </Box>
        </Box>
      </Box>
      {analysis.error_count > 0 && (
        <Box className="flex flex-col gap-4 w-full">
          <Heading as="h6">Detailed Issues</Heading>
          {analysis.errors.map((error: any, index: number) => (
            <Accordion
              key={index}
              title={
                <div className='flex items-center gap-2'>
                  {error.error_type === 'error' ? (
                    <WarningCircle weight="fill" className="text-red-500" />
                  ) : error.error_type === 'warning' ? (
                    <Warning weight="fill" className="text-yellow-500" />
                  ) : (
                    <Info weight="fill" className="text-blue-500" />
                  )}{' '}
                  {error.probable_cause}
                </div>
              }
              wrapperClassName="w-full h-full relative"
            >
              <Box className="mt-2 space-y-3 ml-9 bg-gray-700 rounded-lg p-3">
                <Box>
                  <Text fontWeight="bold" className="text-gray-400">
                    Error Message:
                  </Text>
                  <Text className="text-gray-300">{error.error_message}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" className="text-gray-400">
                    Probable Cause:
                  </Text>
                  <Text className="text-gray-300">{error.probable_cause}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" className="text-gray-400">
                    Suggested Fixes:
                  </Text>
                  <ul className="list-disc pl-4 space-y-2">
                    {error.suggested_fixes.map((fix: string, fixIndex: number) => (
                      <li key={fixIndex} className="text-gray-300">
                        {fix}
                      </li>
                    ))}
                  </ul>
                </Box>
              </Box>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};
